<template>
	<div>

		<h3>Instructions</h3>

		<div class="mb-4 mt-4">

			<h4>Fields</h4>

			<div class="d-flex justify-content-between">
				<ul v-for="column in numColumns" :class="'col-'+(12 / numColumns)">

					<template v-for="(field, index) in fields">

						<li v-if="(index >= (column - 1) * Math.floor(fields.length / numColumns)) && (index < column * Math.floor(fields.length / numColumns))">

							<span class="font-weight-bold" v-html="stripInlineEventHandlers(field.name)"></span>
							<span v-if="'required' in field && field.required" class="text-danger"> &#10033;</span>
							<span v-if="'description' in field && field.description">
								&nbsp;
								(<span v-html="stripInlineEventHandlers(field.description)"></span>)
							</span>

						</li>
					</template>
				</ul>
			</div>

			<div class="mt-2">
				<slot name="notes"></slot>
			</div>
		</div>

		<div class="d-flex flex-row justify-content-between align-items-end">

			<h4>Example</h4>

			<button class="btn btn-primary btn-sm mb-3" v-on:click="downloadTemplate">
				<i class="fa fa-download"></i>
				Download Template
			</button>

		</div>

		<div style="overflow-x: auto;">
			<table class="spreadsheet-table w-100 mb-3">

				<tr>
					<th></th>
					<th v-for="(field, index) in fields">
						{{numAsChar(index + 1)}}
					</th>
				</tr>

				<tr>
					<th></th>
					<td v-for="(field, index) in fields">
						{{field.name}}
					</td>
				</tr>

				<tr v-for="(row, index) in example">
					<th>{{index + 1}}</th>
					<td v-for="field in row">{{field}}</td>
				</tr>

			</table>
		</div>

		<div class="mt-5">

			<h3 class="card-title mt-2">Upload CSV</h3>

			<div class="d-flex justify-content-between align-items-baseline flex-wrap">
				<input type="file" class="form-control" :class="{'col-10': submitVisible}" v-on:change="readFile">
				<div v-if="submitVisible" class="col-2">
					<button class="btn btn-primary" :disabled="!fileRows.length || fileHasInvalidRows || working" v-on:click="$emit('submit')">
						Next
						<i v-if="!working" class="fa fa-arrow-right"></i>
						<div v-else class="spinner-border text-primary"></div>
					</button>
				</div>
			</div>

			<div v-if="fileHasInvalidRows" class="alert alert-danger mt-3 mb-0" role="alert">
				File contains errors in one or more rows. <a href="#" v-on:click.prevent="downloadFileWithErrors">Click here</a>
				to download a copy of your file with more information.  Please fix these errors and re-upload your file before continuing.
			</div>

		</div>

	</div>
</template>

<script>

export default {

	name: 'UploadCsv',

	props: [
		"fields",          // [ { name: 'Example',  description: 'This is an example'/false,   required: true/false, examples: ['row1_value', 'row2_value']  } ]
		"submitVisible",   // should we display the submit button?
		"working",         // display a loading symbol next to the submit button?
	],
	data() {
		return {
			numColumns: 3,
			file: false,

			fileHeaders: [],
			fileRows: [],
			fileIssues: {},

			fileHasInvalidRows: false,
			errorColumnName: "Error(s)"
		}
	},
	computed: {
		example() {
			let rows = []
			this.fields.forEach((field, fieldIndex) => {
				if ('examples' in field) {
					field.examples.forEach((value, rowIndex) => {
						if (rowIndex >= rows.length)
							rows.push(Array(this.fields.length))
						rows[rowIndex][fieldIndex] = value
					})
				}
			})
			return rows
		}
	},
	methods: {
		downloadFileWithErrors() {
			/* Downloads a copy of the uploaded file, with a new column for errors */
			const csvHeaders = [this.errorColumnName, ...this.fileHeaders] // append error column
			const csvBody = this.fileRows.map((row, index) => {
				return (index in this.fileIssues) ? [this.fileIssues[index], ...row] : ['', ...row]
			})
			const newCSV = [csvHeaders, ...csvBody]
			this.exportToCSV('projects_with_errors.csv', newCSV)
		},

		numAsChar(num) {
			/* Converts a number to a character (ex. 0 -> A, 1 -> B...) */
			// UNNECESSARY : DELETE IN FAVOR OF METHOD IN UTILITIES FILE
			return (num + 9).toString(36).toUpperCase()
		},

		downloadTemplate() {
			/* Download the template.csv file provided in the example */
			const titleRow = [this.fields.map(field => field.name)]
			const template = titleRow.concat(this.example)
			this.exportToCSV('template.csv', template)
		},

		readFile(event) {
			/* Triggered when a file is uploaded */
			const vm = this

			//====================================================================

			// GET THE FILE AND VALIDATE TYPE

			if (event.target.files.length > 1)
				return

			this.file = event.target.files[0]
			const reader = new FileReader();

			const allowedMimes = [
				"text/plain",
				"text/csv",
				"application/vnd.ms-excel"
			]

			if (allowedMimes.indexOf(this.file.type) == -1) {
				this.alertFail("Invalid file format! File must be a CSV")
				return
			}

			//====================================================================

			// READ THE FILE AND VALIDATE CONTENT

			reader.onload = (theFile => {
				return e => {

					const csvObj = vm.CSVToArray(e.target.result)

					// Validate that we have at least one data row
					if (csvObj.length == 0) {
						vm.alertFail("File must have at least one data row!")
						return
					}

					//===============================================

					// PARSE HEADER AND VALIDATE FIELDS

					// Note: Headers provided in user file may be out of order
					const fileHeaders = csvObj[0]

					// Map the file column index to the "proper" column index
					let fileIndexToNewIndex = {}
					let fieldsMissing = []

					vm.fields.forEach((field, index) => {
						let headerIndex = fileHeaders.findIndex(name => field.name == name)
						if (headerIndex != -1)
							fileIndexToNewIndex[headerIndex] = index
						else if ('required' in field && field.required) {
							fieldsMissing.push(field.name)
						}
					})

					if (fieldsMissing.length) {
						vm.alertFail("File is missing columns: " + fieldsMissing.join('. '))
						return
					}

					// We ensure that all headers are in OUR order
					vm.fileHeaders = vm.fields.map(field => field.name)

					// Remove header row from csvObj
					csvObj.splice(0, 1)

					//===============================================

					// PARSE ROWS

					// Remove all completely empty rows
					let removeIndices = []
					csvObj.forEach((row, index) => {
						if (row.every(val => val === ''))
							removeIndices.push(index)
					})

					for (let i = removeIndices.length-1; i >= 0; i--)
						csvObj.splice(removeIndices[i], 1)

					// Check if last row is a blank, if so remove it
					if (csvObj[csvObj.length-1].length == 1)
						csvObj.splice(csvObj.length-1,1)


					// Validate that all cells are of the specified type
					const fileIndexToField = fileHeaders.map(name => {
						return this.fields.find(field => field.name == name)
					})

					// Build this out with VALID rows only
					vm.fileRows = []

					csvObj.forEach((row, rowIndex) => {
						let isValid = true                              // is this row valid? Check each cell and update comparatively
						let issues = []                                 // list of issues wrong with the row
						let newRow = Array(vm.fileHeaders.length)       // The new row, built in the proper order

						// Now check each cell
						// Note that we do not know the order of the file's cells, but we want the output to be in our own order
						// Therefore they must be mapped.  Recall that this mapping is done in fileIndexToField
						row.forEach((cell, index) => {

							// Skip this column if it does not map to a valid index!
							if (!(index in fileIndexToNewIndex))
								return

							// Which column is our current cell in?
							const field = fileIndexToField[index]

							// process each as its designated type if provided, and mark the column cell as invalid if needed
							if (field && 'type' in field && field.type) {
								const cleaned = cell.toUpperCase().trim()

								if (field.type == 'bool') {
									if (['TRUE', 'FALSE'].includes(cleaned))
										cell = (cleaned == 'TRUE')
									else
										cell = false
									/*
									else {
										isValid = false
										issues.push("'"+field.name+"' must be TRUE or FALSE")
									}
									 */
								}

								else if (field.type == 'number') {
									if (!isNaN(cleaned))
										cell = Number(cleaned)
									else {
										isValid = false
										issues.push("'"+field.name+"' must be a valid number")
									}
								}

								else if (field.type == 'province') {

									let province = this.provincesList.find(p => p.text.toUpperCase() == cleaned || p.value == cleaned)
									if (!province)
										province = this.statesList.find(s => s.text.toUpperCase() == cleaned || s.value == cleaned)

									if (province)
										cell = province.value       // always return province code
									else {
										isValid = false
										issues.push("'"+field.name+"' must be a valid province or state")
									}

								}

								else if (field.type == 'country') {

									let country = this.countriesList.find(c => c.text.toUpperCase() == cleaned || c.value == cleaned)
									if (country)
										cell = country.text       // always return country name (Canada, United States, etc)
									else {
										isValid = false
										issues.push("'"+field.name+"' must be a valid country")
									}
								}
							}


							if (!isValid)
								vm.fileHasInvalidRows = true

							// save the row value in case it was modified
							const newIndex = fileIndexToNewIndex[index]
							newRow[newIndex] = cell
						})

						if (issues.length)
							vm.fileIssues[rowIndex] = issues.join('. ')

						//newRow.unshift(issues.join('. '))
						vm.fileRows.push(newRow)
					})

					vm.$emit('input', vm.fileHeaders, vm.fileRows)
				};

			}) (this.file);

			reader.readAsText(this.file);
		}
	}
}
</script>

<style type="text/css" scoped>

	.right-margin {
		margin-right: 20px;
	}

	.inner {
		flex-grow: 1
	}

	button.close {
		position: absolute;
		top: 0px;
		right: 5px;
		padding: 5px;
	}
</style>